// FILE: src/pages/StudentNewTicketPage.jsx
// "Ask for help" (plan WP-8, MTO 10.1): the student picks one of the three
// kinds of support (a keyboard-accessible radio group, each option with a
// plain-language hint), describes the problem and sends it. On success the
// new request opens with a confirmation. Server failures are shown with
// errorMessage (never err.message).
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { createTicket } from "../features/supportSlice";
import { PageHeader, visuallyHidden } from "../components/ui";
import { TICKET_CATEGORIES, categoryIcon } from "../components/support/ticketDisplay";
import { ticketCategoryHint, ticketCategoryLabel } from "../i18n/labels";
import { errorMessage, messageFor } from "../i18n/errors";

export const DESCRIPTION_MAX = 2000;

function CategoryOption({ value, selected }) {
  const Icon = categoryIcon(value);
  return (
    <FormControlLabel
      value={value}
      control={<Radio sx={{ mt: -0.75, ml: -0.5 }} />}
      label={
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
            <Icon aria-hidden sx={{ color: "primary.main" }} />
            <Typography component="span" sx={{ fontWeight: 650 }}>
              {ticketCategoryLabel(value)}
            </Typography>
          </Stack>
          {/* A pause between the option's name and its hint for screen readers. */}
          <Box component="span" sx={visuallyHidden}>
            .{" "}
          </Box>
          <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
            {ticketCategoryHint[value]}
          </Typography>
        </Box>
      }
      sx={{
        m: 0,
        alignItems: "flex-start",
        // 2px border when chosen; the padding gives the pixel back so nothing moves.
        p: selected ? "15px" : "16px",
        borderRadius: 3,
        border: selected ? "2px solid" : "1px solid",
        borderColor: selected ? "primary.main" : "zun.inputBorder",
        bgcolor: selected ? "zun.primaryTint" : "background.paper",
        "&:hover": { borderColor: "primary.main" },
        "&:has(.Mui-focusVisible)": { outline: "3px solid", outlineColor: "zun.focus", outlineOffset: 2 },
        "& .MuiFormControlLabel-label": { minWidth: 0 }
      }}
    />
  );
}

export default function StudentNewTicketPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const creating = useSelector((state) => state.support.creating);

  const preset = new URLSearchParams(search).get("category");
  const [category, setCategory] = useState(TICKET_CATEGORIES.includes(preset) ? preset : "");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const categoryRef = useRef(null);
  const descriptionRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setServerError(null);
    const next = {};
    if (!category) next.category = messageFor("INVALID_CATEGORY");
    if (!description.trim()) next.description = "Describe what you need help with.";
    setErrors(next);
    if (next.category) {
      categoryRef.current?.querySelector("input")?.focus();
      return;
    }
    if (next.description) {
      descriptionRef.current?.focus();
      return;
    }

    const result = await dispatch(createTicket({ category, description: description.trim() }));
    if (createTicket.fulfilled.match(result)) {
      const id = result.payload?._id;
      navigate(id ? `/student/support/tickets/${id}` : "/student/support", { state: { created: true } });
    } else {
      setServerError(result.payload || result.error || "INTERNAL_ERROR");
    }
  };

  const count = description.length;

  return (
    <Box sx={{ maxWidth: 900 }}>
      <PageHeader
        breadcrumbs={[{ label: "Support", to: "/student/support" }, { label: "New request" }]}
        title="Ask for help"
        description="Tell us what's going on and we'll reply within 48 hours, by email and on your Support page."
      />

      <Card>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack component="form" spacing={3.5} onSubmit={submit} noValidate>
            {serverError && (
              <Alert severity="error" role="alert">
                {errorMessage(serverError)}
              </Alert>
            )}

            <FormControl component="fieldset" required error={Boolean(errors.category)} sx={{ minWidth: 0 }}>
              <FormLabel
                component="legend"
                id="ticket-category-label"
                sx={{ color: "text.primary", fontWeight: 650, fontSize: "1.125rem", mb: 0.5, "&.Mui-focused": { color: "text.primary" } }}
              >
                What do you need help with?
              </FormLabel>
              <Typography sx={{ color: "text.secondary", mb: 1.5 }}>
                Pick the closest match. We pass it to the right team either way.
              </Typography>
              <RadioGroup
                ref={categoryRef}
                name="category"
                aria-labelledby="ticket-category-label"
                aria-describedby={errors.category ? "ticket-category-error" : undefined}
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setErrors((er) => ({ ...er, category: undefined }));
                }}
                sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}
              >
                {TICKET_CATEGORIES.map((c) => (
                  <CategoryOption key={c} value={c} selected={category === c} />
                ))}
              </RadioGroup>
              {errors.category && <FormHelperText id="ticket-category-error">{errors.category}</FormHelperText>}
            </FormControl>

            <TextField
              id="ticket-description"
              inputRef={descriptionRef}
              label="Describe what's happening"
              multiline
              minRows={6}
              required
              fullWidth
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors((er) => ({ ...er, description: undefined }));
              }}
              error={Boolean(errors.description)}
              slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX } }}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}
                >
                  <span>
                    {errors.description ||
                      "Say which lesson or page, what you were doing and any message you saw. Never include your password."}
                  </span>
                  <Box component="span" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                    {count} of {DESCRIPTION_MAX} characters
                  </Box>
                </Box>
              }
            />

            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.5}
              sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" } }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", color: "text.secondary" }}>
                <ScheduleOutlinedIcon aria-hidden />
                <Typography sx={{ color: "text.secondary" }}>We reply within 48 hours.</Typography>
              </Stack>
              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
                <Button component={RouterLink} to="/student/support" variant="outlined">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating}
                  startIcon={creating ? <CircularProgress size={18} color="inherit" aria-hidden /> : <SendOutlinedIcon />}
                >
                  {creating ? "Sending…" : "Send request"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
